/**
 * OWM response normalization — provider quirks → canonical weather dataShape.
 *
 * SERVER-SIDE ONLY. Validates the raw OWM payload (already Zod-parsed) and maps
 * it to the metrics declared in DATA_SHAPE_KEYS['weather.current']. All conversion
 * and rounding happens here; `glanceView` receives clean, rounded numbers.
 *
 * Immutability: returns a fresh object, never mutates the input.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Raw OWM response schema (validates the upstream payload before use).
// SERVER-SIDE ONLY — the client never sees raw provider payloads (ADR-002 §5).
// ---------------------------------------------------------------------------

/**
 * Zod schema for the OpenWeatherMap "current weather" response.
 * Ref: https://openweathermap.org/current#example_JSON
 *
 * We validate only the fields we consume; additional OWM fields are permitted
 * (passthrough) so future OWM additions don't break the adapter.
 *
 * The `weather` array must have at least one entry — OWM always sends at least
 * one weather descriptor; an empty array is a malformed or unexpected response.
 */
export const rawWeatherSchema = z.object({
  main: z.object({
    temp: z.number(),
    feels_like: z.number().optional(),
    humidity: z.number().int().optional(),
  }),
  weather: z.array(
    z.object({
      id: z.number().int(),
      main: z.string(),
      description: z.string().optional(),
      icon: z.string().optional(),
    }),
  ).min(1),
  wind: z
    .object({
      speed: z.number(),
      deg: z.number().optional(),
    })
    .optional(),
  dt: z.number().int(),
  name: z.string().optional(),
});

export type RawWeather = z.infer<typeof rawWeatherSchema>;

// ---------------------------------------------------------------------------
// Normalized weather data shape — the canonical output of normalize().
// Keys must match DATA_SHAPE_KEYS['weather.current'].
// ---------------------------------------------------------------------------

/**
 * Normalized weather metrics. Exported so glanceView and tests can reference
 * the type without importing from the raw shape.
 *
 * `vsYesterdayF` is null when no yesterday comparison is available (e.g. first
 * fetch, or yesterday data was not provided). Clients must treat null as "no delta"
 * and omit the delta slot rather than show a fake 0.
 */
export interface WeatherData {
  /** Temperature rounded to integer °F. */
  readonly tempF: number;
  /** Temperature in °C, rounded to 1 decimal place. */
  readonly tempC: number;
  /** OWM condition string from the first weather entry (e.g. "Clear", "Rain"). */
  readonly condition: string;
  /**
   * Delta vs yesterday in °F (rounded int). Null when no yesterday value is
   * available — callers must omit the delta slot, never synthesize a 0 delta.
   */
  readonly vsYesterdayF: number | null;
  /** Humidity percentage (integer). */
  readonly humidityPct: number;
  /**
   * Wind speed in mph. Null when the `wind` field is absent (OWM sometimes
   * omits it for certain station types).
   */
  readonly windMph: number | null;
}

/** Optional context injected alongside the raw payload (server-side). */
export interface NormalizeContext {
  /** Yesterday's temperature in °F from the cache / historical source. */
  readonly yesterdayTempF?: number;
}

/**
 * Map an OWM raw payload → canonical WeatherData.
 *
 * Pure: returns a fresh object, never mutates `raw`.
 * Total: every missing optional field has a safe fallback (null).
 */
export function normalize(raw: RawWeather, context: NormalizeContext = {}): WeatherData {
  const tempF = Math.round(raw.main.temp);
  const tempC = Math.round(((raw.main.temp - 32) * 5) / 9 * 10) / 10;
  const condition = raw.weather[0]?.main ?? 'Unknown';
  const humidityPct = raw.main.humidity ?? 0;
  const windMph = raw.wind !== undefined ? Math.round(raw.wind.speed * 10) / 10 : null;
  const vsYesterdayF =
    context.yesterdayTempF !== undefined ? tempF - Math.round(context.yesterdayTempF) : null;

  return {
    tempF,
    tempC,
    condition,
    vsYesterdayF,
    humidityPct,
    windMph,
  };
}
